/**
 * TrialBanner — small reminder banner in the admin sidebar that surfaces
 * the trial-ends countdown for the current tenant.
 *
 * Visibility rules:
 *   - Only shown to authenticated admins of a SaaS tenant (clinic_id != 'default').
 *   - Hidden when:
 *       • the clinic has no trial_ends_at (legacy install or already on paid plan)
 *       • the clinic already has a Stripe Connect account (acct_xxx)
 *       • >7 days remaining and the user clicked X within the past 24h
 *         (sessionStorage, persisted across tab reloads).
 *   - Color escalates: blue >7d, amber 7d→3d, red ≤3d, gray when expired.
 *   - Clicking the "Add payment method" CTA jumps to /admin/settings#stripe.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Clock, CreditCard, X, AlertTriangle } from 'lucide-react';

const SS_KEY = 'tg_trial_banner_dismissed_until';

function daysUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const ms = t - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function TrialBanner() {
  const [clinic, setClinic] = useState(null);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const v = sessionStorage.getItem(SS_KEY);
      // We remember the dismissal for 24 hours, even across tab reloads, so
      // the admin isn't nagged on every page navigation. After 24h it'll show
      // again — and the palette will already have escalated (e.g. amber when
      // they hit ≤7 days) so the next show is impossible to ignore.
      return v && Number(v) > Date.now();
    } catch (_) { return false; }
  });
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [meRes, paymentsRes] = await Promise.allSettled([
          api.get('/clinic/me'),
          api.get('/clinic/me/payments'),
        ]);
        if (!mounted) return;
        if (meRes.status === 'fulfilled') {
          setClinic(meRes.value?.data || null);
        }
        if (paymentsRes.status === 'fulfilled' && paymentsRes.value?.data?.stripe_account_id) {
          setStripeConnected(true);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  if (!clinic || dismissed) return null;
  if (clinic.clinic_id === 'default') return null;        // legacy single-tenant
  if (stripeConnected) return null;                       // already on paid path
  if (!clinic.trial_ends_at) return null;                 // no trial → nothing to show

  const days = daysUntil(clinic.trial_ends_at);
  if (days === null) return null;

  // Pick palette by urgency.
  let palette = {
    bg: 'bg-[#003D80]/10', border: 'border-[#003D80]/20', icon: 'text-[#003D80]',
    title: 'text-[#003D80]', body: 'text-fg-muted', btn: 'bg-[#003D80] text-white hover:bg-[#002B5C]',
    Icon: Clock,
  };
  let title;
  let cta = 'Add payment method';
  if (days <= 0) {
    palette = {
      bg: 'bg-[#B83A3A]/10', border: 'border-[#B83A3A]/30', icon: 'text-[#B83A3A]',
      title: 'text-[#B83A3A]', body: 'text-fg-muted', btn: 'bg-[#B83A3A] text-white hover:bg-[#8a2929]',
      Icon: AlertTriangle,
    };
    title = days === 0 ? 'Trial ends today' : `Trial expired ${Math.abs(days)}d ago`;
    cta = 'Restore access — add payment';
  } else if (days <= 3) {
    palette = {
      bg: 'bg-[#B83A3A]/8', border: 'border-[#B83A3A]/25', icon: 'text-[#B83A3A]',
      title: 'text-[#B83A3A]', body: 'text-fg-muted', btn: 'bg-[#B83A3A] text-white hover:bg-[#8a2929]',
      Icon: AlertTriangle,
    };
    title = `Trial ends in ${days} day${days === 1 ? '' : 's'}`;
  } else if (days <= 7) {
    palette = {
      bg: 'bg-[#FFB000]/15', border: 'border-[#FFB000]/35', icon: 'text-[#B8860B]',
      title: 'text-[#B8860B]', body: 'text-fg-muted', btn: 'bg-[#FFB000] text-[#0F1B2D] hover:bg-[#FFC833]',
      Icon: Clock,
    };
    title = `Trial ends in ${days} days`;
  } else {
    // >7 days — show a soft blue banner with a less prominent CTA, dismissable.
    title = `Trial ends in ${days} days`;
  }

  const dismiss = () => {
    try {
      // Re-show in 24h. Always re-shows on day-7 boundary because palette changes.
      const next = Date.now() + 24 * 60 * 60 * 1000;
      sessionStorage.setItem(SS_KEY, String(next));
    } catch (_) { /* noop */ }
    setDismissed(true);
  };

  const jumpToStripe = () => {
    // Land them on settings; the Payments card auto-scrolls into view via the
    // browser's native fragment behaviour.
    nav('/admin/settings#stripe-connect-card');
    // Soft scroll the in-page hash after the route renders.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="stripe-connect-card"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
  };

  return (
    <div
      className={`relative mx-3 mt-3 mb-2 px-3 py-2.5 rounded-lg border ${palette.bg} ${palette.border}`}
      data-testid="trial-banner">
      <div className="flex items-start gap-2">
        <palette.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${palette.icon}`}/>
        <div className="min-w-0 flex-1">
          <div className={`text-xs font-semibold leading-tight ${palette.title}`} data-testid="trial-banner-title">
            {title}
          </div>
          <div className={`text-[11px] mt-0.5 ${palette.body}`}>
            {days > 0
              ? <>Plan: <span className="font-medium">{(clinic.plan || 'solo')}</span></>
              : <>Read-only access starts soon — keep your data flowing.</>}
          </div>
          <button
            onClick={jumpToStripe}
            className={`mt-2 w-full text-[11px] font-semibold px-2.5 py-1.5 rounded transition inline-flex items-center justify-center gap-1.5 ${palette.btn}`}
            data-testid="trial-banner-cta">
            <CreditCard className="w-3 h-3"/> {cta}
          </button>
        </div>
        {days > 7 && (
          <button
            onClick={dismiss}
            className="text-fg-muted hover:text-fg p-0.5"
            aria-label="Dismiss reminder"
            data-testid="trial-banner-dismiss">
            <X className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
    </div>
  );
}
